import { useEffect, useState } from 'react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormHelperText from '@material-ui/core/FormHelperText';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import Alert from '@material-ui/lab/Alert';
import { UserManagementClient } from '../api';

const GITHUB_USERNAME_REGEX =
  /^[a-zA-Z0-9](?:[a-zA-Z0-9]|-(?=[a-zA-Z0-9])){0,38}$/;

const useStyles = makeStyles(theme => ({
  readOnlyField: {
    backgroundColor: theme.palette.action.hover,
    borderRadius: theme.shape.borderRadius,
  },
  dialogSection: {
    marginBottom: theme.spacing(2),
  },
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
}));

export interface UserRowData {
  name: string;
  namespace: string;
  displayName: string;
  email?: string;
  memberOf: string[];
  entityRef: string;
  profileUrl: string;
  githubUsername?: string;
}

interface EditGithubUsernameDialogProps {
  open: boolean;
  user: UserRowData | null;
  onClose: () => void;
  onSuccess: (updated: {
    entityRef: string;
    githubUsername: string | null;
    email: string;
  }) => void;
  client: UserManagementClient;
}

export function EditGithubUsernameDialog({
  open,
  user,
  onClose,
  onSuccess,
  client,
}: EditGithubUsernameDialogProps) {
  const classes = useStyles();

  const [loadingInitial, setLoadingInitial] = useState(false);
  const [githubUsername, setGithubUsername] = useState('');
  const [revision, setRevision] = useState('');
  const [resolvedEmail, setResolvedEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !user) {
      setGithubUsername('');
      setRevision('');
      setResolvedEmail('');
      setErrorMessage(null);
      return;
    }

    let isMounted = true;
    setLoadingInitial(true);
    setErrorMessage(null);

    // Read the persisted mapping and revision; Catalog annotations may still be stale.
    client
      .getGithubUsername(user.name, user.namespace)
      .then(res => {
        if (!isMounted) return;
        setGithubUsername(res.githubUsername || '');
        setRevision(res.revision);
        setResolvedEmail(res.email);
        setLoadingInitial(false);
      })
      .catch(err => {
        if (!isMounted) return;
        setErrorMessage(
          err instanceof Error
            ? err.message
            : 'Failed to load GitHub username mapping from server.',
        );
        // Fallback to current Catalog state
        setGithubUsername(user.githubUsername || '');
        setResolvedEmail(user.email || '');
        setLoadingInitial(false);
      });

    return () => {
      isMounted = false;
    };
  }, [open, user, client]);

  if (!user) return null;

  const trimmedInput = githubUsername.trim();
  const isUsernameFormatValid =
    trimmedInput === '' || GITHUB_USERNAME_REGEX.test(trimmedInput);

  const handleSave = async () => {
    if (!isUsernameFormatValid) return;

    setSaving(true);
    setErrorMessage(null);

    try {
      // A blank username removes the mapping; revision protects against stale saves.
      const res = await client.updateGithubUsername(user.name, user.namespace, {
        githubUsername: trimmedInput || null,
        revision,
      });

      setSaving(false);
      onSuccess({
        entityRef: user.entityRef,
        githubUsername: res.githubUsername,
        email: res.email,
      });
      onClose();
    } catch (err) {
      setSaving(false);
      // Keep dialog open with entered data so the user can fix and retry
      setErrorMessage(
        err instanceof Error
          ? err.message
          : 'Failed to save GitHub username. Please try again.',
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="edit-github-username-dialog-title"
    >
      <DialogTitle id="edit-github-username-dialog-title">
        <Box className={classes.dialogTitle}>
          <span>Edit GitHub Username</span>
          {loadingInitial && <CircularProgress size={20} />}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {errorMessage && (
          <Box mb={2}>
            <Alert severity="error">{errorMessage}</Alert>
          </Box>
        )}

        <Box className={classes.dialogSection}>
          <Typography variant="caption" color="textSecondary" gutterBottom>
            HR PROFILE (READ-ONLY)
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Full Name"
                value={user.displayName}
                fullWidth
                size="small"
                variant="outlined"
                InputProps={{
                  readOnly: true,
                  className: classes.readOnlyField,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Corporate Email"
                value={resolvedEmail || user.email || '—'}
                fullWidth
                size="small"
                variant="outlined"
                InputProps={{
                  readOnly: true,
                  className: classes.readOnlyField,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Entity Ref"
                value={user.entityRef}
                fullWidth
                size="small"
                variant="outlined"
                InputProps={{
                  readOnly: true,
                  className: classes.readOnlyField,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Groups"
                value={
                  user.memberOf.length > 0
                    ? user.memberOf.map(g => g.split('/').pop()).join(', ')
                    : 'No group'
                }
                fullWidth
                size="small"
                variant="outlined"
                InputProps={{
                  readOnly: true,
                  className: classes.readOnlyField,
                }}
              />
            </Grid>
          </Grid>
        </Box>

        <Box mt={3} className={classes.dialogSection}>
          <Typography variant="subtitle2" gutterBottom>
            GitHub Username (Editable)
          </Typography>
          <TextField
            fullWidth
            autoFocus
            variant="outlined"
            placeholder="e.g. octocat"
            value={githubUsername}
            onChange={e => setGithubUsername(e.target.value)}
            disabled={saving || loadingInitial}
            error={!isUsernameFormatValid}
            helperText={
              !isUsernameFormatValid
                ? 'GitHub username may only contain alphanumeric characters or single hyphens, cannot start or end with a hyphen, and must not exceed 39 characters.'
                : undefined
            }
          />
          <FormHelperText>
            💡 Leave blank and click Save to remove the GitHub username mapping
            for this user.
          </FormHelperText>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving} color="default">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={saving || loadingInitial || !isUsernameFormatValid}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
